import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate, getServiceTypeLabel, getStatusBadgeClass, getStatusLabel, truncateText } from '../utils/helpers';
import { SERVICE_TYPES, QUOTATION_STATUS } from '../utils/constants';

const Quotations = () => {
  const navigate = useNavigate();
  const { quotations, loadingQuotations } = useApp();
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    serviceType: ''
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredQuotations = useMemo(() => quotations.filter(q => {
    const matchesSearch = !filters.search || 
      q.customerName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      q.phone?.includes(filters.search) ||
      q.quotationNumber?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = !filters.status || q.status === filters.status;
    const matchesService = !filters.serviceType || q.serviceType === filters.serviceType;
    
    return matchesSearch && matchesStatus && matchesService;
  }), [quotations, filters]);

  const { page, setPage, pageSize, setPageSize, total, totalPages, pagedItems } = usePagination(
    filteredQuotations,
    { persistKey: 'quotations', resetKey: `${filters.search}|${filters.status}|${filters.serviceType}` }
  );

  const columns = [
    {
      key: 'quotationNumber',
      label: 'Ref',
      render: (value) => (
        <span className="font-mono text-sm font-semibold text-primary-600">{value || '—'}</span>
      )
    },
    {
      key: 'customerName',
      label: 'Macmiilka',
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{row.phone}</p>
        </div>
      )
    },
    {
      key: 'serviceType',
      label: 'Adeegga',
      render: (value) => (
        <span className="text-sm">{getServiceTypeLabel(value)}</span>
      )
    },
    {
      key: 'fromLocation',
      label: 'Goobaha',
      render: (value, row) => (
        <div className="text-sm">
          <p>📍 {truncateText(value, 15)}</p>
          <p>📍 {truncateText(row.toLocation, 15)}</p>
        </div>
      )
    },
    {
      key: 'movingDate',
      label: 'Taariikhda',
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      label: 'Xaalada',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    }
  ];

  const statusOptions = Object.entries(QUOTATION_STATUS).map(([value, { label }]) => ({
    value,
    label
  }));

  const pendingCount = quotations.filter((q) => q.status === 'pending').length;
  const approvedCount = quotations.filter((q) => q.status === 'approved' || q.status === 'invoiced').length;

  return (
    <div className="space-y-4">
      <PageHero
        eyebrow="Codsiyada"
        title="Quatations"
        subtitle="Dhammaan quotation-yada macaamiisha"
        stats={[
          { label: 'Wadarta', value: quotations.length },
          { label: 'Sugaya', value: pendingCount, hint: 'Weli la ansixin' },
          { label: 'La ansixiyay', value: approvedCount },
          { label: 'La soo bandhigay', value: filteredQuotations.length, hint: 'Kadib filter' }
        ]}
      />

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Raadi..."
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
          <Select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            options={statusOptions}
            placeholder="Xaalada"
          />
          <Select
            name="serviceType"
            value={filters.serviceType}
            onChange={handleFilterChange}
            options={SERVICE_TYPES}
            placeholder="Adeegga"
          />
        </div>
      </Card>

      <Card padding={false}>
        <div className="hidden md:block overflow-x-auto">
          <Table
            columns={columns}
            data={pagedItems}
            loading={loadingQuotations}
            onRowClick={(row) => navigate(`/admin/quotations/${row.id}`)}
            emptyMessage="Quatation lama helin"
          />
        </div>

        <div className="md:hidden space-y-3 p-3">
          {!loadingQuotations && pagedItems.length === 0 && (
            <p className="text-center text-gray-500 py-8">Quatation lama helin</p>
          )}
          {pagedItems.map((q) => (
            <div 
              key={q.id}
              onClick={() => navigate(`/admin/quotations/${q.id}`)}
              className="bg-slate-50 rounded-2xl p-4 border border-slate-100 cursor-pointer hover:shadow-md hover:bg-white transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium">{q.customerName}</p>
                  <p className="text-sm text-gray-500">{q.phone}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(q.status)}`}>
                  {getStatusLabel(q.status)}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p>📍 {truncateText(q.fromLocation, 20)} → {truncateText(q.toLocation, 20)}</p>
                <p className="mt-1">📅 {formatDate(q.movingDate)}</p>
              </div>
              <div className="mt-2">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  {getServiceTypeLabel(q.serviceType)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>
    </div>
  );
};

export default Quotations;